import React, { useEffect, useRef, useState } from 'react';
import LogoDesktop from '../../../../images/lp/googleV2/MilanLogoDesktop.svg';
import LogoMWeb from '../../../../images/lp/googleV2/MilanLogoMWeb.svg';
import './StickyCTA.css';
import { Link } from 'gatsby';
import SpecialsBanner from '../../../header/SpecialsBanner';
import { PromoCountdown } from '../../../header/PromoCountdown';

interface DesktopStickyCTAProps {
    showCountdown?: boolean;
    heroIsFirst?: boolean;
    alwaysVisible?: boolean;
}

const LogoLinks = () => (
    <>
        <Link to="/">
            <img src={LogoDesktop} alt="Milan Logo" className="d-none d-lg-block googlelp-desktop-logo" />
        </Link>
        <Link to="/">
            <img src={LogoMWeb} alt="Milan Logo" className="d-lg-none d-block googlelp-mobile-logo" />
        </Link>
    </>
);

const HeaderCTA = () => (
    <a
        href="#googlelp-quote"
        id="googlelp-header-cta"
        className="rounded-pill border-none mcl-font-proxima button-cta d-none d-lg-flex"
    >
        Get started
    </a>
);

export function DesktopStickyCTA({ showCountdown, heroIsFirst = true, alwaysVisible = false }: DesktopStickyCTAProps) {
    const [visible, setVisible] = useState(alwaysVisible);
    const stickyRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (alwaysVisible) {
            setVisible(true);
            return;
        }

        const hero = document.getElementById('hero-cta') || document.querySelector('.hero-cta');
        if (!hero || typeof IntersectionObserver === 'undefined') return;

        // When heroIsFirst is false, also observe PromoModule so the sticky
        // hides whenever either it or the hero is in view.
        const firstSection = !heroIsFirst ? document.getElementById('promo-module') : null;

        // Initialize conservatively — sticky stays hidden until the observer
        // fires. firstSectionVisible defaults true only when firstSection
        // exists so it doesn't block the control variant.
        let heroVisible = true;
        let firstSectionVisible = firstSection !== null;

        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.target === hero) heroVisible = entry.isIntersecting;
                    else if (firstSection && entry.target === firstSection) firstSectionVisible = entry.isIntersecting;
                });
                setVisible(!heroVisible && !firstSectionVisible);
            },
            { threshold: 0 }
        );

        io.observe(hero);
        if (firstSection) io.observe(firstSection);
        return () => io.disconnect();
    }, [heroIsFirst, alwaysVisible]);

    useEffect(() => {
        if (!showCountdown) return;
        const el = stickyRef.current;
        const page = document.getElementById('googlelp-v2-page');
        if (!el || !page || typeof ResizeObserver === 'undefined') return;

        const sync = () => {
            const height = el.getBoundingClientRect().height;
            page.style.setProperty('--sticky-cta-height', `${height}px`);
        };

        sync();
        const ro = new ResizeObserver(sync);
        ro.observe(el);
        return () => {
            ro.disconnect();
            page.style.removeProperty('--sticky-cta-height');
        };
    }, [showCountdown]);

    if (showCountdown) {
        return (
            <div
                ref={stickyRef}
                className={`googlelp-desktop-sticky-cta-countdown ${visible ? 'is-visible' : ''}`}
                aria-hidden={!visible}
            >
                <div className="googlelp-desktop-sticky-bar px-4">
                    <LogoLinks />
                    <HeaderCTA />
                </div>
                <div className="googlelp-sticky-countdown-section">
                    <PromoCountdown />
                </div>
            </div>
        );
    }

    return (
        <div
            className={`googlelp-desktop-sticky-cta-default px-4 justify-between align-items-center ${visible ? 'is-visible' : ''}`}
            aria-hidden={!visible}
        >
            <LogoLinks />
            <SpecialsBanner textColor="milan-text-navy" className="text-center" />
            <HeaderCTA />
        </div>
    );
}

export default DesktopStickyCTA;
